import { 
  Search, 
  Database, 
  Brain, 
  FileText, 
  Upload, 
  RefreshCw, 
  Zap,
  Star,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Filter,
  Settings,
  BookOpen,
  Target
} from 'lucide-react';
import React, { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Agent } from '../../../../stores/agentStore';

interface AgentQdrantDialogProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
}

interface VectorDocument {
  id: string;
  content: string;
  metadata: {
    title: string;
    type: 'knowledge' | 'procedure' | 'example' | 'template';
    tags: string[];
    source: string;
    created: string;
    lastAccessed: string;
    accessCount: number;
    relevanceScore?: number;
  };
  embedding: number[];
}

interface SearchResult {
  document: VectorDocument;
  similarity: number;
  context: string;
  reasoning: string;
}

interface Collection {
  name: string;
  description: string;
  size: number;
  dimension: number;
  status: 'active' | 'indexing' | 'error';
  lastUpdated: string;
  documentCount: number;
}

interface QueryStats {
  totalQueries: number;
  avgResponseTime: number;
  successRate: number;
  topQueries: { query: string; count: number; avgSimilarity: number }[];
  recentQueries: { query: string; timestamp: string; resultCount: number; maxSimilarity: number }[];
}

const AgentQdrantDialog: React.FC<AgentQdrantDialogProps> = ({ isOpen, onClose, agent }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedCollection, setSelectedCollection] = useState('agent-knowledge');
  const [isSearching, setIsSearching] = useState(false);
  const [newDocument, setNewDocument] = useState({ title: '', content: '', tags: '', type: 'knowledge' as const });
  const [isUploading, setIsUploading] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    type: 'all',
    minSimilarity: 0.7,
    maxResults: 10,
    includeTags: '',
    excludeTags: ''
  });
  const [activeTab, setActiveTab] = useState('search');

  // Mock data for demonstration
  const [collections] = useState<Collection[]>([
    {
      name: 'agent-knowledge',
      description: 'General knowledge base for agent reasoning',
      size: 2.3,
      dimension: 1536,
      status: 'active',
      lastUpdated: '2025-01-15T10:30:00Z',
      documentCount: 1247
    },
    {
      name: 'procedures',
      description: 'Standard operating procedures and workflows',
      size: 1.8,
      dimension: 1536,
      status: 'active',
      lastUpdated: '2025-01-14T16:45:00Z',
      documentCount: 892
    },
    {
      name: 'examples',
      description: 'Code examples and implementation patterns',
      size: 3.1,
      dimension: 1536,
      status: 'indexing',
      lastUpdated: '2025-01-15T08:15:00Z',
      documentCount: 1678
    },
    {
      name: 'templates',
      description: 'Document and code templates',
      size: 0.9,
      dimension: 1536,
      status: 'active',
      lastUpdated: '2025-01-13T14:20:00Z',
      documentCount: 456
    }
  ]);

  const [queryStats] = useState<QueryStats>({
    totalQueries: 15847,
    avgResponseTime: 125,
    successRate: 97.8,
    topQueries: [
      { query: 'authentication best practices', count: 234, avgSimilarity: 0.89 },
      { query: 'error handling patterns', count: 189, avgSimilarity: 0.85 },
      { query: 'database optimization', count: 156, avgSimilarity: 0.91 },
      { query: 'security vulnerabilities', count: 143, avgSimilarity: 0.87 },
      { query: 'testing strategies', count: 128, avgSimilarity: 0.83 }
    ],
    recentQueries: [
      { query: 'React component patterns', timestamp: '2025-01-15T11:45:00Z', resultCount: 12, maxSimilarity: 0.94 },
      { query: 'API rate limiting', timestamp: '2025-01-15T11:30:00Z', resultCount: 8, maxSimilarity: 0.88 },
      { query: 'deployment strategies', timestamp: '2025-01-15T11:15:00Z', resultCount: 15, maxSimilarity: 0.92 },
      { query: 'performance monitoring', timestamp: '2025-01-15T11:00:00Z', resultCount: 6, maxSimilarity: 0.85 },
      { query: 'data validation', timestamp: '2025-01-15T10:45:00Z', resultCount: 11, maxSimilarity: 0.90 }
    ]
  });

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    
    // Simulate API call to Qdrant
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 700));
    
    // Mock search results
    const mockResults: SearchResult[] = [
      {
        document: {
          id: 'doc_001',
          content: 'Authentication in modern web applications should follow OAuth 2.0 standards with proper token management, secure storage, and refresh token rotation. Implement CSRF protection and validate all tokens server-side.',
          metadata: {
            title: 'OAuth 2.0 Implementation Guide',
            type: 'knowledge',
            tags: ['authentication', 'security', 'oauth'],
            source: 'knowledge-base',
            created: '2024-12-01T10:00:00Z',
            lastAccessed: '2025-01-15T09:30:00Z',
            accessCount: 47
          },
          embedding: []
        },
        similarity: 0.94,
        context: 'Authentication and security implementation',
        reasoning: 'High relevance due to exact match on authentication concepts and security practices'
      },
      {
        document: {
          id: 'doc_002',
          content: 'Error handling best practices include proper logging, user-friendly messages, and graceful degradation. Use structured error responses, implement circuit breakers for external services, and ensure proper error boundaries in React applications.',
          metadata: {
            title: 'Error Handling Patterns',
            type: 'procedure',
            tags: ['error-handling', 'logging', 'best-practices'],
            source: 'procedures',
            created: '2024-11-15T14:30:00Z',
            lastAccessed: '2025-01-14T16:20:00Z',
            accessCount: 62
          },
          embedding: []
        },
        similarity: 0.87,
        context: 'Error management and system reliability',
        reasoning: 'Relevant for robust application development and user experience'
      },
      {
        document: {
          id: 'doc_003',
          content: 'React component optimization techniques including memo, useMemo, and useCallback for performance. Implement proper prop drilling avoidance, use React.lazy for code splitting, and optimize re-renders with proper dependency arrays.',
          metadata: {
            title: 'React Performance Optimization',
            type: 'example',
            tags: ['react', 'performance', 'optimization'],
            source: 'examples',
            created: '2024-10-20T11:15:00Z',
            lastAccessed: '2025-01-15T08:45:00Z',
            accessCount: 89
          },
          embedding: []
        },
        similarity: 0.82,
        context: 'Frontend development and performance',
        reasoning: 'Contains practical examples and implementation details for React optimization'
      }
    ];

    setSearchResults(mockResults);
    setIsSearching(false);
  };

  const handleUploadDocument = async () => {
    if (!newDocument.title || !newDocument.content) return;
    
    setIsUploading(true);
    
    // Simulate document upload and embedding generation
    await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1000));
    
    // Reset form
    setNewDocument({ title: '', content: '', tags: '', type: 'knowledge' });
    setIsUploading(false);
    
    // Show success feedback (in real implementation, this would be a toast)
    console.log('Document uploaded successfully');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600';
      case 'indexing': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'indexing': return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'error': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatBytes = (bytes: number) => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes * 1024 * 1024) / Math.log(1024));
    return Math.round((bytes * 1024 * 1024) / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Vector Knowledge Base - {agent.name}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="search" className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Semantic Search
            </TabsTrigger>
            <TabsTrigger value="collections" className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              Collections
            </TabsTrigger>
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Add Knowledge
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="search" className="flex-1 space-y-4 min-h-0">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="search-query">Semantic Search Query</Label>
                  <div className="flex gap-2">
                    <Input
                      id="search-query"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Enter your question or search terms..."
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button onClick={handleSearch} disabled={isSearching}>
                      {isSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      Search
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="collection-select">Collection</Label>
                  <select
                    id="collection-select"
                    value={selectedCollection}
                    onChange={(e) => setSelectedCollection(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    aria-label="Select collection"
                  >
                    {collections.map((collection) => (
                      <option key={collection.name} value={collection.name}>
                        {collection.name} ({collection.documentCount} docs)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <Card>
                <CardHeader className="" hasCardAction={false}>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Search Filters
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <select
                        value={searchFilters.type}
                        onChange={(e) => setSearchFilters({...searchFilters, type: e.target.value})}
                        className="w-full px-2 py-1 border rounded text-sm"
                        aria-label="Filter by type"
                      >
                        <option value="all">All Types</option>
                        <option value="knowledge">Knowledge</option>
                        <option value="procedure">Procedures</option>
                        <option value="example">Examples</option>
                        <option value="template">Templates</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Min Similarity</Label>
                      <Input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={searchFilters.minSimilarity}
                        onChange={(e) => setSearchFilters({...searchFilters, minSimilarity: parseFloat(e.target.value)})}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Max Results</Label>
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        value={searchFilters.maxResults}
                        onChange={(e) => setSearchFilters({...searchFilters, maxResults: parseInt(e.target.value)})}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Include Tags</Label>
                      <Input
                        value={searchFilters.includeTags}
                        onChange={(e) => setSearchFilters({...searchFilters, includeTags: e.target.value})}
                        placeholder="tag1,tag2"
                        className="text-sm"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <div className="space-y-4">
                {searchResults.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    Found {searchResults.length} results
                  </div>
                )}
                {searchResults.map((result) => (
                  <Card key={result.document.id} className="border-l-4 border-l-blue-500">
                    <CardHeader className="pb-3" hasCardAction={false}>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <CardTitle className="text-base">{result.document.metadata.title}</CardTitle>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Badge variant="outline" className="text-xs">
                              {result.document.metadata.type}
                            </Badge>
                            <span>•</span>
                            <span>{result.document.metadata.source}</span>
                            <span>•</span>
                            <span>Accessed {result.document.metadata.accessCount} times</span>
                          </div>
                        </div>
                        <div className="text-right space-y-1">
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500" />
                            <span className="text-sm font-medium">{(result.similarity * 100).toFixed(1)}%</span>
                          </div>
                          <Badge variant={result.similarity > 0.9 ? "default" : result.similarity > 0.8 ? "secondary" : "outline"}>
                            {result.similarity > 0.9 ? "Excellent" : result.similarity > 0.8 ? "Good" : "Fair"} Match
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <h4 className="text-sm font-medium mb-1">Content Preview</h4>
                        <p className="text-sm text-muted-foreground">{result.document.content}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-1">Context</h4>
                        <p className="text-sm text-muted-foreground">{result.context}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-1">AI Reasoning</h4>
                        <p className="text-sm text-blue-600 italic">{result.reasoning}</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                          {result.document.metadata.tags.map((tag) => (
                            <Badge key={tag} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <BookOpen className="w-3 h-3 mr-1" />
                            View Full
                          </Button>
                          <Button size="sm" variant="outline">
                            <Target className="w-3 h-3 mr-1" />
                            Use Context
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {searchResults.length === 0 && searchQuery && !isSearching && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      No results found for "{searchQuery}". Try adjusting your search terms or filters.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="collections" className="flex-1 space-y-4 min-h-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {collections.map((collection) => (
                <Card key={collection.name}>
                  <CardHeader className="pb-3" hasCardAction={false}>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-base">{collection.name}</CardTitle>
                      <div className={`flex items-center gap-1 ${getStatusColor(collection.status)}`}>
                        {getStatusIcon(collection.status)}
                        <span className="text-xs capitalize">{collection.status}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">{collection.description}</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Documents:</span>
                        <span className="font-medium">{collection.documentCount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Size:</span>
                        <span className="font-medium">{formatBytes(collection.size)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Dimension:</span>
                        <span className="font-medium">{collection.dimension}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Updated:</span>
                        <span className="font-medium">{formatDate(collection.lastUpdated)}</span>
                      </div>
                    </div>
                    <Separator />
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1">
                        <Settings className="w-3 h-3 mr-1" />
                        Configure
                      </Button>
                      <Button size="sm" variant="outline">
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="upload" className="flex-1 space-y-4 min-h-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="" hasCardAction={false}>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Add Document
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="doc-title">Title</Label>
                    <Input
                      id="doc-title"
                      value={newDocument.title}
                      onChange={(e) => setNewDocument({...newDocument, title: e.target.value})}
                      placeholder="Document title..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="doc-type">Type</Label>
                    <select
                      id="doc-type"
                      value={newDocument.type}
                      onChange={(e) => setNewDocument({...newDocument, type: e.target.value as any})}
                      className="w-full px-3 py-2 border rounded-md"
                      aria-label="Document type"
                    >
                      <option value="knowledge">Knowledge</option>
                      <option value="procedure">Procedure</option>
                      <option value="example">Example</option>
                      <option value="template">Template</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="doc-tags">Tags (comma-separated)</Label>
                    <Input
                      id="doc-tags"
                      value={newDocument.tags}
                      onChange={(e) => setNewDocument({...newDocument, tags: e.target.value})}
                      placeholder="tag1, tag2, tag3..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="doc-content">Content</Label>
                    <Textarea
                      id="doc-content"
                      value={newDocument.content}
                      onChange={(e) => setNewDocument({...newDocument, content: e.target.value})}
                      placeholder="Enter document content..."
                      rows={10}
                    />
                  </div>
                  <Button 
                    onClick={handleUploadDocument} 
                    disabled={isUploading || !newDocument.title || !newDocument.content}
                    className="w-full"
                  >
                    {isUploading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    {isUploading ? 'Generating Embeddings...' : 'Add to Knowledge Base'}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="" hasCardAction={false}>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="w-5 h-5" />
                    AI-Powered Features
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <Zap className="h-4 w-4" />
                    <AlertDescription>
                      Documents are automatically processed with AI to generate semantic embeddings for intelligent search and retrieval.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="space-y-3">
                    <h4 className="font-medium">Automatic Processing</h4>
                    <ul className="text-sm space-y-1 text-muted-foreground">
                      <li>• Semantic embedding generation (1536-dim vectors)</li>
                      <li>• Content summarization and key concept extraction</li>
                      <li>• Automatic tag suggestion based on content</li>
                      <li>• Relevance scoring for search optimization</li>
                      <li>• Relationship mapping with existing documents</li>
                    </ul>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <h4 className="font-medium">Supported Formats</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <Badge variant="outline">Markdown</Badge>
                      <Badge variant="outline">Plain Text</Badge>
                      <Badge variant="outline">JSON</Badge>
                      <Badge variant="outline">Code</Badge>
                      <Badge variant="outline">Documentation</Badge>
                      <Badge variant="outline">Procedures</Badge>
                    </div>
                  </div>

                  <Button variant="outline" className="w-full">
                    <Upload className="w-4 h-4 mr-2" />
                    Bulk Upload Files
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="flex-1 space-y-4 min-h-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-3" hasCardAction={false}>
                  <CardTitle className="text-sm">Total Queries</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{queryStats.totalQueries.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">All-time searches</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3" hasCardAction={false}>
                  <CardTitle className="text-sm">Avg Response Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{queryStats.avgResponseTime}ms</div>
                  <p className="text-xs text-muted-foreground">Vector search speed</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3" hasCardAction={false}>
                  <CardTitle className="text-sm">Success Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{queryStats.successRate}%</div>
                  <p className="text-xs text-muted-foreground">Queries with results</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="" hasCardAction={false}>
                  <CardTitle className="text-base">Top Search Queries</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-64">
                    <div className="space-y-3">
                      {queryStats.topQueries.map((query, index) => (
                        <div key={index} className="space-y-2">
                          <div className="flex justify-between items-start">
                            <span className="text-sm font-medium">{query.query}</span>
                            <Badge variant="outline">{query.count} queries</Badge>
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>Avg Similarity</span>
                              <span>{(query.avgSimilarity * 100).toFixed(1)}%</span>
                            </div>
                            <Progress value={query.avgSimilarity * 100} className="h-1" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="" hasCardAction={false}>
                  <CardTitle className="text-base">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-64">
                    <div className="space-y-3">
                      {queryStats.recentQueries.map((query, index) => (
                        <div key={index} className="border-l-2 border-blue-200 pl-3 space-y-1">
                          <div className="flex justify-between items-start">
                            <span className="text-sm font-medium">{query.query}</span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(query.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <div className="flex gap-4 text-xs text-muted-foreground">
                            <span>{query.resultCount} results</span>
                            <span>{(query.maxSimilarity * 100).toFixed(1)}% max similarity</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AgentQdrantDialog;
